import { clsx } from 'clsx';
import { AriaAttributes, AriaRole } from 'react';
import {
  DallasTextAs,
  DallasTextColor,
  DallasTextAlign,
  DallasTextVariant,
  DALLAS_TEXT_COLOR_CLASSNAME_SEGMENTS,
} from '../types/typography';

import styles from './DallasText.module.scss';

export type Props = AriaAttributes & {
  // Identifier
  id?: string;
  // Role for assistive technologies
  role?: AriaRole;
  className?: string;
  // Sets a valid color for the text
  color?: DallasTextColor;
  // The text
  children?: React.ReactNode;
  // Adds a line through the text
  lineThrough?: boolean;
  // Prevents text for wrapping if text is too long
  noWrap?: boolean;
  // Sets `noWrap` for compact viewports
  'compact-noWrap'?: boolean;
  // Sets the alignment of the inner text
  align?: DallasTextAlign;
  // Sets `align` for compact viewports
  'compact-align'?: DallasTextAlign;
  // Sets a different set of props to the text based on variant value
  variant?: DallasTextVariant;
  // Sets `variant` for compact viewports
  'compact-variant'?: DallasTextVariant;
} & (
    | {
        // Allows rendering as a diferent tag depending on semantics
        as?: DallasTextAs;
      }
    | {
        as: 'abbr';
        title: string;
      }
  );

export default function DallasText({
  id,
  color,
  children,
  className,
  as: Root = 'span',
  lineThrough,
  align = 'inherit',
  ['compact-align']: alignCompact,
  noWrap,
  ['compact-noWrap']: noWrapCompact,
  variant = 'body',
  ['compact-variant']: variantCompact,
  ...props
}: Props) {
  return (
    <Root
      id={id}
      className={clsx(
        className,
        styles.root,
        color && styles[`color-${DALLAS_TEXT_COLOR_CLASSNAME_SEGMENTS[color]}`],
        lineThrough && styles.lineThrough,
        noWrap && styles.noWrap,
        noWrapCompact && styles['compact-noWrap'],
        styles[`align-${align}`],
        alignCompact && styles[`compact-align-${alignCompact}`],
        styles[`variant-${variant}`],
        variantCompact && styles[`compact-variant-${variantCompact}`],
      )}
      {...props}
    >
      {children}
    </Root>
  );
}
